import { useCallback, useState } from "react";
import { Platform, RefreshControl } from "react-native";

import { palette } from "@/constants/colors";

import { useColorScheme } from "./use-color-scheme";

type RefreshFn = () => Promise<unknown>;

/**
 * Pull-to-refresh for native ScrollViews.
 * On web, prefer a full page reload — RefreshControl is not attached.
 */
export function usePullToRefresh(
    onRefreshData: RefreshFn,
    options?: { tintColor?: string },
) {
    const [refreshing, setRefreshing] = useState(false);
    const colorScheme = useColorScheme();
    const tintColor =
        options?.tintColor ??
        (colorScheme === "dark" ? palette.dark.primary : palette.light.primary);

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await onRefreshData();
        } finally {
            setRefreshing(false);
        }
    }, [onRefreshData]);

    const refreshControl = (
        <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
                void onRefresh();
            }}
            tintColor={tintColor}
            colors={[tintColor]}
        />
    );

    if (Platform.OS === "web") {
        return {
            refreshing: false,
            refreshControl: undefined,
        };
    }
    return {
        refreshing,
        refreshControl,
    };
}
